import { useEffect, useRef, useState } from 'react';
import settings from '@/content/settings.json';
import { Heading } from './Heading';
import { spacingClass } from '@/utils/spacing';
import { useI18n } from '@/components/_vs/i18n/I18nProvider';

/**
 * Mappa OpenStreetMap interattiva (Leaflet, caricato via import dinamico —
 * solo le pagine con questo blocco pagano il suo peso) con la posizione del
 * locale. Nessun campo indirizzo proprio: legge lat/lng da Impostazioni →
 * Dati attività, la stessa fonte usata dal JSON-LD — un solo locale, un
 * solo posto dove aggiornarlo. Zoom con rotellina disattivato di proposito
 * (altrimenti intercetta lo scroll della pagina appena il cursore ci passa
 * sopra); resta trascinabile e zoomabile coi pulsanti +/-.
 * `layout: 'grid'` = contenuta in max-w-6xl, angoli arrotondati secondo
 * `roundness` (px); `'widescreen'` = a tutta larghezza, sempre senza angoli
 * arrotondati (coerente col nome — un bordo squadrato edge-to-edge).
 * @param {{ title?: string, subtitle?: string, zoom?: number, height?: number, showDirectionsButton?: boolean, layout?: 'grid'|'widescreen', roundness?: number, spacing?: string, headingLevel?: number }} props
 */
export function MapBlock({
  title, subtitle, zoom = 15, height = 420, showDirectionsButton = true,
  layout = 'grid', roundness = 12, spacing = 'normal', headingLevel = 2,
}) {
  const { t } = useI18n();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const isWidescreen = layout === 'widescreen';

  const business = settings.business || {};
  const lat = parseFloat(business.lat);
  const lng = parseFloat(business.lng);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    if (!hasCoords || !mapRef.current || mapInstanceRef.current) return undefined;
    let cancelled = false;

    Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')])
      .then(([L]) => {
        if (cancelled || !mapRef.current) return;
        const accent = getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim() || '#8C6240';
        const icon = L.divIcon({
          className: '',
          html: `<svg width="30" height="40" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg"><path d="M16 0C7.163 0 0 7.163 0 16c0 11 16 26 16 26s16-15 16-26C32 7.163 24.837 0 16 0z" fill="${accent}"/><circle cx="16" cy="16" r="6" fill="#fff"/></svg>`,
          iconSize: [30, 40],
          iconAnchor: [15, 40],
        });

        const map = L.map(mapRef.current, { center: [lat, lng], zoom, scrollWheelZoom: false });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(map);
        // keyboard: false — il marker è decorativo (la vera azione, "Ottieni
        // indicazioni", è già un link con testo proprio); di default Leaflet
        // lo rende un target da tastiera senza nome accessibile, violazione
        // aria-command-name trovata con Lighthouse.
        L.marker([lat, lng], { icon, keyboard: false, alt: business.name || '' }).addTo(map);
        mapInstanceRef.current = map;
      })
      .catch(() => { if (!cancelled) setLoadError(true); });

    return () => {
      cancelled = true;
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
    };
  }, [hasCoords, lat, lng, zoom]);

  if (!hasCoords) {
    if (!import.meta.env?.DEV) return null;
    return (
      <section className="px-6 md:px-16 py-24 md:py-32 text-center">
        <p className="text-sm text-theme-text-subtle">Blocco Mappa: imposta Latitudine/Longitudine in Impostazioni → Dati attività.</p>
      </section>
    );
  }

  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  const mapEl = (
    <div
      className="relative overflow-hidden border border-theme-border"
      style={{ height, borderRadius: isWidescreen ? 0 : `${roundness}px` }}
    >
      <div ref={mapRef} className="w-full h-full bg-theme-bg-subtle" />
      {loadError && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-theme-text-subtle bg-theme-bg-subtle">
          {t('map.unavailable')}
        </div>
      )}
      {showDirectionsButton && (
        <a
          href={directionsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 right-4 z-[400] inline-flex items-center px-5 py-2.5 rounded-lg bg-theme-accent text-theme-accent-text text-sm font-medium tracking-wide shadow-vs-md"
        >
          {t('map.getDirections')}
        </a>
      )}
    </div>
  );

  return (
    <section className={spacingClass(spacing)}>
      {(title || subtitle) && (
        <div className="px-6 md:px-16 max-w-6xl mx-auto">
          <div className="max-w-xl mb-10">
            {title && <Heading level={headingLevel} className="font-heading text-h1 font-light text-theme-text mb-4">{title}</Heading>}
            {subtitle && <p className="text-body-lg text-theme-text-muted leading-relaxed">{subtitle}</p>}
          </div>
        </div>
      )}
      {isWidescreen ? mapEl : (
        <div className="px-6 md:px-16">
          <div className="max-w-6xl mx-auto">{mapEl}</div>
        </div>
      )}
    </section>
  );
}
